import type { Api } from "../../../services/api";
import i18n from "../../../config/i18n";
import { getItemTranslation } from "../../../domains/app/utils/getItemTranslation";
import { normalizeLanguage } from "../../../domains/app/utils/langUtils";

type PickerProfession = Pick<
  Api["get"]["/webapp/public/professions"]["output"]["body"][number],
  "uid" | "translations" | "onboardingVisibility"
>;

/** The short list's order. A `main` profession absent from it comes after. */
const PRIORITY_PROFESSION_UIDS: string[] = [
  "physician",
  "medical-resident",
  "medical-student",
  "pharmacist",
  "veterinarian",
  "registered-nurse",
  "professional-in-healthcare-industry",
  "psychologist",
  "physiotherapist",
  "audiologist",
  "Researcher",
];

/** Whether the picker lists the longer list ("Show more" was tapped). */
export type ProfessionListState = "collapsed" | "expanded";

/**
 * What the onboarding picker lists. Collapsed: the short `main` list in its
 * curated order, plus the selected profession when it comes from the longer
 * list (an RPPS prefill, a step back), so the selection stays on screen.
 * Expanded: every `main` and `more` profession, the `more` ones alphabetical
 * in the language being read. A `hidden` profession is never listed.
 * `offersMore` is true while collapsed and the longer list adds something.
 */
export function pickerProfessions<T extends PickerProfession>({
  professions,
  selected,
  list,
}: {
  professions: T[];
  selected: string | null;
  list: ProfessionListState;
}): { listed: T[]; all: T[]; offersMore: boolean } {
  const priorityIndex = new Map(
    PRIORITY_PROFESSION_UIDS.map((uid, i) => [uid, i])
  );
  const rank = (uid: string) => {
    const index = priorityIndex.get(uid);
    return index === undefined ? PRIORITY_PROFESSION_UIDS.length : index;
  };
  const main = professions
    .filter((p) => p.onboardingVisibility === "main")
    .sort((a, b) => rank(a.uid) - rank(b.uid));
  const more = professions
    .filter((p) => p.onboardingVisibility === "more")
    .sort((a, b) =>
      // The reader's language, not the browser's: they can differ.
      getItemTranslation(a).localeCompare(
        getItemTranslation(b),
        normalizeLanguage(i18n.resolvedLanguage)
      )
    );
  const all = [...main, ...more];
  const pinned = more.filter((profession) => profession.uid === selected);
  return {
    listed: list === "expanded" ? all : [...main, ...pinned],
    all,
    offersMore: list === "collapsed" && more.length > pinned.length,
  };
}
